import { Routes, Route, Navigate, Outlet, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { MainLayout } from '@/components/layout/MainLayout';
import { MobileRouteGuard } from '@/components/mobile/MobileRouteGuard';
import { NativePushBootstrap } from '@/components/mobile/NativePushBootstrap';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { RequireAuth, SessionLoading, SessionUnreachable } from '@/components/auth/RequireAuth';
import { SwitchRoleToContinue } from '@/components/routing/SwitchRoleToContinue';
import { LocaleProvider } from '@/i18n/LocaleProvider';
import { api } from '@/services/api';

// Parent-facing surfaces stay eagerly imported — they're the hot path
// and the main bundle already pays for them. Auth + landing too: needed
// at first paint for unauthenticated visitors.
import { LoginPage } from '@/pages/LoginPage';
import { ApplyLandingPage } from '@/pages/apply/ApplyLandingPage';
import { VerifyEmailPage } from '@/pages/apply/VerifyEmailPage';
// #416: the public provider drop-off. Lazy, because the people who reach it
// are medical-practice staff arriving from an email once — they should not pay
// for the rest of the app's bundle to hand in one PDF.
const ProviderUploadPage = lazy(() => import('@/pages/ProviderUploadPage').then((m) => ({ default: m.ProviderUploadPage })));
import { ApplicationWizard } from '@/pages/apply/ApplicationWizard';
import { RenewalWizard } from '@/pages/renew/RenewalWizard';
import { ApplicationStatusPage } from '@/pages/apply/ApplicationStatusPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { SetNewPasswordPage } from '@/pages/SetNewPasswordPage';
import { MagicLinkPage } from '@/pages/MagicLinkPage';
import { isNative } from '@/lib/platform';
import { DashboardPage } from '@/pages/DashboardPage';
import { TasksPage } from '@/pages/TasksPage';
import { BillsPage } from '@/pages/BillsPage';
import { CirclePage } from '@/pages/CirclePage';
import { CalendarPage } from '@/pages/CalendarPage';
import { HealthPage } from '@/pages/HealthPage';
import { CommunityPage } from '@/pages/CommunityPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { HelpPage } from '@/pages/HelpPage';
import { MfaVerifyPage } from '@/pages/MfaVerifyPage';
import { NotFoundPage } from '@/pages/NotFoundPage';

// Chat is heavy (LiveKit + react-markdown + remark-gfm pull ~700kB of
// vendor weight). Since #2266 the conversation lives on the home and
// /chat/:id is only the deep-link address, so it stays lazy.
const ChatPage = lazy(() => import('@/pages/ChatPage').then((m) => ({ default: m.ChatPage })));

// Admin pages — none of these are loaded for parent/trfer/helper roles.
const AdminPage = lazy(() => import('@/pages/AdminPage').then((m) => ({ default: m.AdminPage })));
const ConversationReviewPage = lazy(() => import('@/pages/admin/conversation-review/ConversationReviewPage').then((m) => ({ default: m.ConversationReviewPage })));
const AdminUsersPage = lazy(() => import('@/pages/admin/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })));
const SystemHealthPage = lazy(() => import('@/pages/admin/SystemHealthPage').then((m) => ({ default: m.SystemHealthPage })));
const AdminActivityPage = lazy(() => import('@/pages/admin/AdminActivityPage').then((m) => ({ default: m.AdminActivityPage })));
const AdminFeedbackPage = lazy(() => import('@/pages/admin/AdminFeedbackPage').then((m) => ({ default: m.AdminFeedbackPage })));
const AdminHandoffsPage = lazy(() => import('@/pages/admin/AdminHandoffsPage').then((m) => ({ default: m.AdminHandoffsPage })));
const AdminConversationsPage = lazy(() => import('@/pages/admin/AdminConversationsPage').then((m) => ({ default: m.AdminConversationsPage })));
// Restored 2026-09-21 (DECISIONS 2026-09-20 amendment, Victor's ruling):
// `/admin/handoff-config` mounts this component again, non-embedded. The
// TRFer Settings Handoff tab keeps its own `embedded` mount
// (`TRFerSettingsPage.tsx`) — one component, two shells, deliberately.
const AdminHandoffConfigPage = lazy(() => import('@/pages/admin/AdminHandoffConfigPage').then((m) => ({ default: m.AdminHandoffConfigPage })));
const AdminTranslationReportsPage = lazy(() => import('@/pages/admin/TranslationReportsPage').then((m) => ({ default: m.TranslationReportsPage })));
const LegalPublishPage = lazy(() => import('@/pages/admin/LegalPublishPage').then((m) => ({ default: m.LegalPublishPage })));
const PlatformSettingsPage = lazy(() => import('@/pages/admin/PlatformSettingsPage').then((m) => ({ default: m.PlatformSettingsPage })));
const TRFerUsersPage = lazy(() => import('@/pages/admin/TRFerUsersPage').then((m) => ({ default: m.TRFerUsersPage })));

// TRFer pages — only loaded when role === 'trfer'.
const TRFerPage = lazy(() => import('@/pages/TRFerPage').then((m) => ({ default: m.TRFerPage })));
import { LiveConversationGate } from '@/components/trfer/LiveConversationGate';
const ThreadsPage = lazy(() => import('@/pages/trfer/ThreadsPage').then((m) => ({ default: m.ThreadsPage })));
const ThreadParticipationPage = lazy(() => import('@/pages/trfer/ThreadParticipationPage').then((m) => ({ default: m.ThreadParticipationPage })));
const FamiliesPage = lazy(() => import('@/pages/trfer/FamiliesPage').then((m) => ({ default: m.FamiliesPage })));
const FamilyProfilePage = lazy(() => import('@/pages/trfer/FamilyProfilePage').then((m) => ({ default: m.FamilyProfilePage })));
const GrantRequestsPage = lazy(() => import('@/pages/trfer/GrantRequestsPage').then((m) => ({ default: m.GrantRequestsPage })));
const AdminBillLinksPage = lazy(() => import('@/pages/admin/AdminBillLinksPage').then((m) => ({ default: m.AdminBillLinksPage })));
const PartnerHospitalsPage = lazy(() => import('@/pages/trfer/PartnerHospitalsPage').then((m) => ({ default: m.PartnerHospitalsPage })));
const ResourcesPage = lazy(() => import('@/pages/trfer/ResourcesPage').then((m) => ({ default: m.ResourcesPage })));
const ReimbursementsPage = lazy(() => import('@/pages/trfer/ReimbursementsPage').then((m) => ({ default: m.ReimbursementsPage })));
const SpendReviewPage = lazy(() => import('@/pages/trfer/SpendReviewPage').then((m) => ({ default: m.SpendReviewPage })));
const CommunityModerationPage = lazy(() => import('@/pages/trfer/CommunityModerationPage').then((m) => ({ default: m.CommunityModerationPage })));
// The parent's own thread list (#1707) — a different page from the TRFer
// review surface above, so a different name.
const WilliamsPage = lazy(() => import('@/pages/trfer/WilliamsPage').then((m) => ({ default: m.WilliamsPage })));
const TRFerSettingsPage = lazy(() => import('@/pages/trfer/TRFerSettingsPage').then((m) => ({ default: m.TRFerSettingsPage })));
// #3235: Import families from TRF's Google Form application (admins and Lead TRFers).
const FamilyImportsPage = lazy(() => import('@/pages/trfer/FamilyImportsPage').then((m) => ({ default: m.FamilyImportsPage })));
const FamilyImportFamilyPage = lazy(() => import('@/pages/trfer/FamilyImportFamilyPage').then((m) => ({ default: m.FamilyImportFamilyPage })));
const PollsPage = lazy(() => import('@/pages/trfer/PollsPage'));
const PollCreatePage = lazy(() => import('@/pages/trfer/PollCreatePage'));
const PollDetailPage = lazy(() => import('@/pages/trfer/PollDetailPage'));
const ApplicationsQueuePage = lazy(() => import('@/pages/trfer/ApplicationsQueuePage').then((m) => ({ default: m.ApplicationsQueuePage })));
const ApplicationDetailPage = lazy(() => import('@/pages/trfer/ApplicationDetailPage').then((m) => ({ default: m.ApplicationDetailPage })));
const CommitteePackagePage = lazy(() => import('@/pages/trfer/CommitteePackagePage').then((m) => ({ default: m.CommitteePackagePage })));
const AwardsPage = lazy(() => import('@/pages/trfer/AwardsPage').then((m) => ({ default: m.AwardsPage })));
const ReferralCodesPage = lazy(() => import('@/pages/trfer/ReferralCodesPage').then((m) => ({ default: m.ReferralCodesPage })));
const FormsBuilderPage = lazy(() => import('@/pages/trfer/forms/FormsBuilderPage').then((m) => ({ default: m.FormsBuilderPage })));
// CopyEditorPage is no longer routed directly — it renders inside
// FormsBuilderPage's wording view. Its two old addresses redirect there.

/**
 * `/trfer/copy` → the Forms surface's wording view.
 *
 * Family wording became the second view of Forms (Mario's ruling, 2026-08-29).
 * The address is kept alive rather than deleted because `copyDeepLink` has
 * been handing it out since PR-D — "the lede on the expired-link screen" is a
 * sentence nobody wants to say twice, and the link that saves them saying it
 * must not 404. Every existing param survives the hop, `?slot=` above all;
 * `replace` keeps the dead address out of the reader's back button.
 *
 * Took no `scope` prop until 2026-09-20 (admin-section subtraction):
 * `/admin/copy` used to share this component with `scope="admin"`, landing
 * on `/admin/forms`. That mount is gone — `/admin/copy` now redirects flat to
 * `/trfer/forms` — so `/trfer/copy` is the only caller left, and a
 * single-valued `scope` prop was exactly the drift this task's Step 8 exists
 * to catch.
 */
function CopyViewRedirect() {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  next.set('view', 'wording');
  return <Navigate to={`/trfer/forms?${next.toString()}`} replace />;
}

/**
 * `/admin/polls/:id` kept its address through
 * the 2026-09-20 subtraction but lost their admin mounts. A bare <Navigate>
 * would drop the id AND would dead-end an admin at the trfer-only gate that
 * encloses the TRFer address (final whole-branch review, CRITICAL 1) — so
 * each re-reads its param, rebuilds the TRFer address, and hands it to
 * `SwitchRoleToContinue` rather than redirecting straight there.
 */
function AdminPollRedirect() {
  const { id } = useParams();
  return <SwitchRoleToContinue to={`/trfer/polls/${id}`} />;
}

/** Redirect that carries the query string along — for an address whose page
 *  split or moved but whose filters (`?country=`, the cohort lens) still apply. */
export function SearchPreservingRedirect({ to, set, rename }: { to: string; set?: Record<string, string>; rename?: Record<string, string> }) {
  const [params] = useSearchParams();
  // `set` pins the params the destination needs to land on the right view
  // (2026-09-14: `/trfer/queue` → Families' Needs you view keeps its
  // `filter`/`urgency` deep-link params and gains `view=needs-you`).
  // `rename` moves a param the destination spells differently, before `set`.
  const next = new URLSearchParams(params);
  for (const [from, to_] of Object.entries(rename ?? {})) {
    const v = next.get(from);
    if (v !== null) { next.delete(from); next.set(to_, v); }
  }
  for (const [k, v] of Object.entries(set ?? {})) next.set(k, v);
  const qs = next.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
}

// TRFer mobile pages — the lightweight /trfer/m surface for native staff.
const TrferMobileLayout = lazy(() => import('@/components/trfer-mobile/TrferMobileLayout').then((m) => ({ default: m.TrferMobileLayout })));
const TrferTodayPage = lazy(() => import('@/components/trfer-mobile/TrferTodayPage').then((m) => ({ default: m.TrferTodayPage })));
const TrferFamilyLitePage = lazy(() => import('@/components/trfer-mobile/TrferFamilyLitePage').then((m) => ({ default: m.TrferFamilyLitePage })));
const TrferHandoffMobilePage = lazy(() => import('@/components/trfer-mobile/TrferHandoffMobilePage').then((m) => ({ default: m.TrferHandoffMobilePage })));

import { Toaster } from '@/components/ui/toaster';
import { MfaOnboardingModal } from '@/components/auth/MfaOnboardingModal';
import { PermissionGuard } from '@/components/PermissionGuard';
import { ScrollToTop } from '@/components/ScrollToTop';
import { LegalIndexPage } from '@/pages/legal/LegalIndexPage';
import { LegalDocPage } from '@/pages/legal/LegalDocPage';
import { SafetyPage } from '@/pages/legal/SafetyPage';
import { LegalSettingsPage } from '@/pages/settings/LegalSettingsPage';
import { LandingPage } from '@/pages/LandingPage';
import { ReconsentGate } from '@/components/legal/ReconsentGate';
import { ReconsentPage } from '@/pages/legal/ReconsentPage';
import { AccountClosePage } from '@/pages/account/AccountClosePage';
import type { User } from '@/types/api';
import { homeFor } from '@/lib/home-route';
import type { LanguageCode } from '@/config/languages';

// #2753: the language a person picked on the sign-in page (or a `?lang=` link
// carried) is saved to their profile once they are signed in.
const saveSignInLanguage = async (code: LanguageCode): Promise<void> => {
  await api.auth.updateProfile({ language: code });
};

// Shows landing page for visitors; redirects authenticated users to their home.
// "/": a signed-in user goes home, a visitor sees the landing page. While the
// boot session check cannot answer (5xx, network), it shows the same "can't
// reach TRF" state as RequireAuth rather than the landing page (#2657); a 401
// is a plain visitor and still gets the landing page. Exported for tests.
export function RootRoute() {
  const { isAuthenticated, loading, user, sessionCheckFailed } = useAuth();

  if (loading) return <SessionLoading />;

  if (isAuthenticated) {
    return <Navigate to={homeFor(user?.role)} replace />;
  }

  if (sessionCheckFailed) return <SessionUnreachable />;

  return <LandingPage />;
}

// Renders DashboardPage for parents; redirects staff/helpers to their home.
// /dashboard is the parent home. An applicant's home is the application
// (homeFor): the parent home's queries are parent-only, so mounting it for an
// applicant fetched data they cannot read and spent their request budget on
// it (#2382 follow-up). Exported for App.dashboard-route.test.tsx.
export function DashboardRoute() {
  const { user } = useAuth();
  if (user?.role === 'admin') return <Navigate to="/admin" replace />;
  if (user?.role === 'trfer') return <Navigate to={homeFor('trfer')} replace />;
  if (user?.role === 'applicant') return <Navigate to={homeFor('applicant')} replace />;
  return <DashboardPage />;
}

// Role-gated Route wrapper — must be nested inside RequireAuth so that
// unauthenticated users are redirected to /login before this check runs.
// Exported for App.rolegate.test.tsx (B-203).
export function RoleGatedRoute({ allowedRoles }: { allowedRoles: User['role'][] }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  if (!user || !allowedRoles.includes(user.role)) {
    return <Navigate to={homeFor(user?.role)} replace />;
  }

  return <Outlet />;
}

/**
 * A staff route gated on the ENTITLEMENT set rather than the active role.
 *
 * `RoleGatedRoute` above asks "what hat are you wearing right now", which is
 * the right question almost everywhere. It is the wrong one for a surface the
 * SERVER admits by entitlement: `requireTRFerOrAdmin` tests `req.user.roles` —
 * "a TRFer is a TRFer whichever hat they happen to be wearing"
 * (backend/src/middleware/authorization.js:69-88).
 *
 * The copy editor is that surface, and the disagreement was not cosmetic. An
 * admin browsing as a parent is a persona the platform documents
 * (permissionResolver.js:21-23); the server lets them DRAFT and refuses only
 * the publish routes, because `can_publish_copy` resolves against the ACTIVE
 * role (D-CS-5). The SPA bounced them to /dashboard instead — which made
 * D-CS-4's read-only banner unreachable and left D-CS-5's entire carve-out
 * with no consumer. Found by the live walk (B-438).
 *
 * Deliberately NOT a change to `RoleGatedRoute`: widening that would re-gate
 * every staff route at once, with a zero-line diff in anything that inspects
 * declarations — the same shape D-CS-5 refused when it scoped the permission
 * question rather than answering it platform-wide.
 */
export function StaffEntitlementRoute({ allowedRoles }: { allowedRoles: User['role'][] }) {
  const { user, loading } = useAuth();

  if (loading) return null;

  const held = (user as { roles?: string[] } | null)?.roles ?? (user ? [user.role] : []);
  if (!user || !allowedRoles.some((role) => held.includes(role))) {
    return <Navigate to={homeFor(user?.role)} replace />;
  }

  return <Outlet />;
}

function App() {
  const { pathname } = useLocation();
  // Pages that render their own standalone brand chrome (marketing/auth) skip
  // the app-shell wrapper entirely. Auth pages (login/register/forgot/reset/mfa)
  // joined this list in Phase 2.
  const stevensChromeRoutes = [
    '/',
    '/welcome',
    '/privacy',
    '/terms',
    '/sms-terms',
    '/login',
    '/register',
    '/forgot-password',
    '/reset-password',
    '/set-new-password',
    '/auth/magic-link',
    '/mfa',
    '/apply',
    '/verify-email',
  ];
  // #416: the provider drop-off renders its own AuthShell chrome like the
  // pages above, but carries a token segment so it cannot be a literal match.
  const isProviderUpload = pathname.startsWith('/provider-upload/');
  // Standalone marketing/auth pages (incl. the /apply landing) render their OWN
  // full-height layout — no app shell.
  const isStandaloneChrome = stevensChromeRoutes.includes(pathname) || isProviderUpload;
  // The /apply/* subroutes (wizard, status) render INSIDE MainLayout, so they
  // still need the h-full app-shell wrapper for its inner <main> to scroll,
  // even though the applicant flow is otherwise chrome-light. (Collapsing this
  // wrapper for non-standalone routes broke scrolling once — B-107 — so it
  // stays regardless of what else is on the page.)
  // h-full + box-border + overflow-hidden keeps MainLayout sized to the
  // painted viewport so its inner scroll container (<main>) absorbs the rest.
  const wrapperClass = isStandaloneChrome ? '' : 'h-full box-border overflow-hidden';

  return (
    <LocaleProvider onSignInLanguage={saveSignInLanguage}>
      <ScrollToTop />
      <div className={wrapperClass}>
        <Suspense
          fallback={
            <div className="flex items-center justify-center min-h-screen">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          }
        >
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<Navigate to="/apply" replace />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/set-new-password" element={<SetNewPasswordPage />} />
          <Route path="/auth/magic-link" element={<MagicLinkPage />} />
          <Route path="/mfa" element={<MfaVerifyPage />} />
          <Route path="/" element={<RootRoute />} />
          <Route path="/welcome" element={<LandingPage />} />
          <Route path="/legal" element={<LegalIndexPage />} />
          <Route path="/legal/safety" element={<SafetyPage />} />
          <Route path="/legal/:doc" element={<LegalDocPage />} />
          {/* Back-compat aliases for old static URLs */}
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
          <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/sms-terms" element={<Navigate to="/legal/sms" replace />} />

          {/* Family application — public entry points */}
          <Route path="/apply" element={<ApplyLandingPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* #416: a medical provider handing in a diagnosis letter. PUBLIC by
              necessity — a hospital records office holds no TRF account — and
              authorized by the single-use, application-scoped token in the
              path. Registered here, outside RequireAuth, deliberately. */}
          <Route path="/provider-upload/:token" element={<ProviderUploadPage />} />

          {/* Protected routes */}
          <Route
            element={
              <RequireAuth>
                <NativePushBootstrap />
                <MainLayout />
              </RequireAuth>
            }
          >
            {/* Re-consent + closure: authenticated but NOT behind the gate,
                so a blocked user can reach them to re-accept or close out. */}
            <Route path="reconsent" element={<ReconsentPage />} />
            <Route path="account/close" element={<AccountClosePage />} />

            {/* Every normal app-shell page is gated: a blocked user hitting
                any of these is redirected to /reconsent. */}
            <Route element={<ReconsentGate />}>
            {/* Family application — wizard + status (applicant or parent) */}
            <Route element={<RoleGatedRoute allowedRoles={['applicant', 'parent']} />}>
              <Route path="apply/wizard" element={<ApplicationWizard />} />
              <Route path="apply/status" element={<ApplicationStatusPage />} />
              {/* Signature Grant renewal (F-027) — admitted parents only; the
                  RenewalWizard's flow guard bounces non-parents to /dashboard. */}
              <Route path="renew" element={<RenewalWizard />} />
            </Route>
            <Route path="dashboard" element={<DashboardRoute />} />
            <Route element={<RoleGatedRoute allowedRoles={['parent', 'admin']} />}>
              <Route path="chat" element={<ChatPage />} />
              <Route path="chat/:conversationId" element={<ChatPage />} />
              {/* The thread archive moved into Settings › Companion on 2026-09-21.
                  This redirect keeps every link that shipped — the sidebar row
                  that used to point here, the Maya card, bookmarks — landing on
                  the list rather than a 404. */}
              <Route path="conversations" element={<Navigate to="/settings?tab=companion" replace />} />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="tasks/:taskId" element={<TasksPage />} />
              <Route path="bills" element={<BillsPage />} />
              <Route path="bills/:billId" element={<Navigate to="/bills" replace />} />
              {/* Grants are a TAB of Finances (walk verdict 2026-08-21), not a
                  destination. This alias keeps the /grants links that shipped
                  in PR #1060 v1 (and anything a parent bookmarked from the
                  walk) landing on that tab instead of a 404. */}
              <Route path="grants" element={<Navigate to="/bills?tab=grants" replace />} />
              <Route path="circle" element={<CirclePage />} />
              <Route path="calendar" element={<CalendarPage />} />
              <Route path="medical" element={<HealthPage />} />
              <Route path="community" element={<CommunityPage />} />
            </Route>
            <Route element={<MobileRouteGuard />}>
              <Route element={<RoleGatedRoute allowedRoles={['admin']} />}>
                <Route path="admin" element={<AdminPage />} />
                <Route path="admin/reviews" element={<ConversationReviewPage />} />
                <Route path="admin/conversations" element={<AdminConversationsPage />} />
                <Route path="admin/users" element={<AdminUsersPage />} />
                <Route path="admin/health" element={<SystemHealthPage />} />
                {/* Absorbed 2026-09-20: Agent status read the cached
                    /admin/stats aggregate; System health probes the agents
                    live over gRPC and shows the dependencies beside them. */}
                <Route path="admin/agents" element={<Navigate to="/admin/health" replace />} />
                <Route path="admin/stats" element={<AdminActivityPage />} />
                <Route path="admin/feedback" element={<AdminFeedbackPage />} />
                {/* Subtracted 2026-09-20: every one of these is reachable in
                    the TRFer desk, which an admin opens with the sidebar's
                    role switcher. The addresses stay so no bookmark and no
                    `to=` literal dangles (route-integrity PHANTOM_CEILING).
                    /admin/signups lands inside the admin-OR-trfer gate
                    (`trfer/users`, below), so a bare redirect resolves for an
                    admin with no role switch needed — a plain <Navigate> is
                    correct there. polls, polls/new and ledger land inside the
                    TRFER-ONLY gate (`RoleGatedRoute allowedRoles={['trfer']}`,
                    further down), which a bare <Navigate> does NOT satisfy for
                    an admin — that dead end was CRITICAL 1 of the final
                    whole-branch review (2026-09-21): the redirect fired, the
                    gate rejected it, and the admin was silently bounced to
                    /admin. Each now hands off through `SwitchRoleToContinue`
                    instead, which asks before switching the reader's active
                    role rather than doing it as a side effect of a stale
                    link. handoff-config is NOT one of these any more — see
                    the route below, restored 2026-09-21. */}
                <Route path="admin/signups" element={<Navigate to="/trfer/users?status=pending" replace />} />
                <Route path="admin/handoffs" element={<AdminHandoffsPage />} />
                {/* Restored 2026-09-21 (DECISIONS 2026-09-20 amendment):
                    Victor ruled staffing mode, office hours, SLA windows and
                    per-signal routing are org-wide operational configuration —
                    real admin data by his own "only real admin data" test,
                    the same class as the feature flags this branch promoted
                    into Platform settings. A real mount, not a redirect and
                    not `SwitchRoleToContinue` — this address is reachable
                    without ever holding the `trfer` role. The TRFer Settings
                    Handoff tab keeps its OWN `embedded` mount of the same
                    component (`TRFerSettingsPage.tsx`); one component in two
                    shells, deliberately, per the amendment. */}
                <Route path="admin/handoff-config" element={<AdminHandoffConfigPage />} />
                <Route path="admin/translation-reports" element={<AdminTranslationReportsPage />} />
                <Route path="admin/legal" element={<LegalPublishPage />} />
                <Route path="admin/settings" element={<PlatformSettingsPage />} />
                {/* Admin-only, so a single mount — the dual admin/trfer mount
                    convention applies only to pages both roles need. */}
                <Route path="admin/bill-links" element={<AdminBillLinksPage />} />
                <Route path="admin/polls"     element={<SwitchRoleToContinue to="/trfer/polls" />} />
                <Route path="admin/polls/new" element={<SwitchRoleToContinue to="/trfer/polls/new" />} />
                {/* #2452: the Ledger was removed 2026-10-09. Its old addresses
                    land on the admin console rather than a 404. */}
                <Route path="admin/ledger"       element={<Navigate to="/admin" replace />} />
                <Route path="admin/ledger/:rest" element={<Navigate to="/admin" replace />} />
                <Route path="admin/polls/:id"      element={<AdminPollRedirect />} />
                {/* Moved to the TRFer desk 2026-09-20 — the partner list is
                    intake reference data every TRFer maintains. */}
                <Route path="admin/partner-hospitals" element={<Navigate to="/trfer/hospitals" replace />} />
              </Route>
            </Route>
            <Route element={<MobileRouteGuard />}>
              {/* /trfer/users is admin OR Lead TRFer, so it lives outside the
                  trfer-only role gate: since 2026-09-14 (#1980) it carries the
                  Assignments view that was /trfer/families/manage, which an
                  admin reached from the admin sidebar. PermissionGuard narrows
                  further to (admin OR lead-trfer + can_assign_trfers) — the
                  same gate the backend's /trfer/users mount enforces (#455). */}
              <Route element={<RoleGatedRoute allowedRoles={['admin', 'trfer']} />}>
                <Route
                  path="trfer/users"
                  element={
                    <PermissionGuard permission="can_assign_trfers">
                      <TRFerUsersPage />
                    </PermissionGuard>
                  }
                />
                {/* Pending signups is the Users roster filtered to
                    status=pending (2026-09-14, #1980). Family assignments
                    moved twice: onto Users as its Assignments view
                    (2026-09-14), then onto Families (wave w1024,
                    2026-10-24), where assignment lives now — the old
                    address lands on the families nobody holds, its `q`
                    intact. Both redirects keep their params. */}
                <Route path="trfer/families/manage" element={<SearchPreservingRedirect to="/trfer/families" set={{ scope: 'unassigned' }} />} />
                <Route path="trfer/signups" element={<SearchPreservingRedirect to="/trfer/users" set={{ status: 'pending' }} />} />
                <Route
                  path="trfer/grants"
                  element={
                    <PermissionGuard permission="can_assign_trfers">
                      <GrantRequestsPage />
                    </PermissionGuard>
                  }
                />
                {/* Ungated since 2026-09-22 (Victor: "a plain TRFer should
                    be able to read the TRF-wide application"). The backend
                    mount opened its READS the same day — requireTRFerOrAdmin
                    for GET, can_review_applications for every write — so a
                    guard here would now be STRICTER than the server and would
                    hide a page that works. Both pages render read-only for a
                    reader without the permission: no decision bar, no send, no
                    field edit, and a banner saying so. */}
                <Route path="trfer/applications" element={<ApplicationsQueuePage />} />
                {/* #3322: the committee meeting screen retired in v1.180.0; its old
                    address lands on Committee instead of reading "meeting" as an
                    application id. The static segment outranks :id. */}
                <Route path="trfer/applications/meeting" element={<Navigate to="/trfer/committee-package" replace />} />
                <Route path="trfer/applications/:id" element={<ApplicationDetailPage />} />
                {/* Plan 2026-10-10 (L3), presentations 2026-10-26: write each family's
                    committee page and download the presentation. Gated: its reads
                    (GET /committee-presentations) and every write need
                    can_review_applications. */}
                <Route
                  path="trfer/committee-package"
                  element={
                    <PermissionGuard permission="can_review_applications">
                      <CommitteePackagePage />
                    </PermissionGuard>
                  }
                />
                {/* /trfer/pipeline ("Queue") and /trfer/offers ("Offers")
                    folded into Applications on 2026-09-13: the per-stage
                    counts are the strip above its toolbar, the outstanding
                    offers are the same list filtered to offer_sent. The
                    hrefs stay as redirects so a bookmark, a desk-lane link
                    or a stale impersonation grant still lands somewhere
                    true. Applications carries the permission guard. */}
                <Route path="trfer/pipeline" element={<Navigate to="/trfer/applications" replace />} />
                <Route path="trfer/offers" element={<Navigate to="/trfer/applications?status=offer_sent" replace />} />
              </Route>
              {/* The family-wording editor (#1039 PR-D) lives OUTSIDE every
                  active-role gate. That placement is the whole point: the
                  groups above gate on the role a person is WEARING, and would
                  bounce the persona D-CS-4 is written for — an admin browsing
                  as a parent — before this guard ever runs. The server admits
                  that caller by entitlement and refuses only the publish
                  routes, so the SPA has to agree or D-CS-5's carve-out has
                  nobody to serve. B-438.

                  NO PermissionGuard, deliberately. Publish rights
                  (`can_publish_copy`) are enforced in-page and by the server; a
                  route guard would bounce a staff user who can legitimately
                  draft, and would replace the read-only case's one useful
                  sentence — "switch to your staff view" — with a dead end. */}
              <Route
                element={<StaffEntitlementRoute allowedRoles={['admin', 'trfer']} />}
              >
                {/* Family wording moved INSIDE the Forms surface as its
                    second view (Mario's ruling, 2026-08-29): every slot in the
                    registry is `apply.*`, i.e. the shell around the application
                    forms. `/trfer/copy` stays a REDIRECT rather than being
                    deleted — `copyDeepLink` has been handing out
                    `/trfer/copy?slot=…` addresses since PR-D, and a pasted link
                    that 404s is how a colleague concludes a string is not
                    editable. `?slot=` rides across untouched.

                    `/admin/copy` redirects flat to `/trfer/forms` (2026-09-20,
                    admin-section subtraction) rather than through
                    `CopyViewRedirect`: the admin SHELL it used to preserve is
                    gone along with `/admin/forms` below, so there is no
                    `?slot=`-preserving destination left to land it in. The
                    address stays only so an old `?slot=` link 404s into the
                    Forms surface instead of the 404 page. */}
                <Route path="trfer/copy" element={<CopyViewRedirect />} />
                <Route path="admin/copy" element={<Navigate to="/trfer/forms" replace />} />
                {/* `trfer/forms` lives in THIS group, not the active-role one
                    above, and that placement moved here on 2026-08-29 with the
                    wording.

                    The wording editor was deliberately outside every
                    active-role gate (B-438): the groups above gate on the role
                    a person is WEARING, and would bounce the persona D-CS-4 is
                    written for — an admin browsing as a parent — before the
                    guard ever runs. Now that the wording is the Forms surface's
                    second view, leaving the builder role-gated would have
                    re-imposed exactly that bounce on every `?slot=` deep link:
                    `/trfer/copy` admitted the caller and `/trfer/forms` turned
                    them away, so the redirect would have narrowed who could
                    follow a link that has been handed out since PR-D.

                    The widening this implies is real and intended: a staff
                    member browsing as a parent can now open the builder, not
                    just the wording. The server is unchanged and still decides
                    — `requireTRFerOrAdmin` gates every /api/forms staff route
                    by entitlement, which is the same basis this guard uses.

                    `admin/forms` redirects to it (2026-09-20, admin-section
                    subtraction): the admin sidebar no longer offers Forms as
                    its own row — an admin reaches it, in the TRFer shell, via
                    the sidebar's role switcher — so the ADMIN-shell mount this
                    address used to preserve no longer exists to land in. */}
                <Route path="trfer/forms" element={<FormsBuilderPage />} />
                <Route path="admin/forms" element={<Navigate to="/trfer/forms" replace />} />
              </Route>
              {/* B-203 / D-W3-3: the family profile is reachable by any staff
                  who can open a family — assigned TRFer, Lead, or admin. The
                  backend (#558) enforces per-family scoping via
                  assertFamilyCaseloadAccess; this gate is only SPA
                  reachability. Its own group so ONLY these routes widen to
                  admin — the rest of /trfer stays trfer-only. Partner
                  hospitals (#1151, moved 2026-09-20) joined it for the same
                  reason: an admin who has not switched into the TRFer role
                  must still reach the routes the backend gates as
                  requireTRFerOrAdmin.
                  Families itself joined on 2026-10-24 (wave w1024): it is
                  where families are managed — assignment, Add a family —
                  and an admin reaches it with a Lead's reach. The server
                  decides what the list holds: every family for an admin or
                  a Lead, the caseload for a plain TRFer. */}
              <Route element={<RoleGatedRoute allowedRoles={['trfer', 'admin']} />}>
                <Route path="trfer/families" element={<FamiliesPage />} />
                <Route path="trfer/families/:familyId" element={<ErrorBoundary><FamilyProfilePage /></ErrorBoundary>} />
                <Route path="trfer/hospitals" element={<PartnerHospitalsPage />} />
              </Route>
              <Route element={<RoleGatedRoute allowedRoles={['trfer']} />}>
              <Route path="trfer" element={<TRFerPage />} />
              {/* Dormant for launch (#2566): reachable only while the live
                  conversation is on, or while a crisis thread is open. */}
              <Route element={<LiveConversationGate fallback="/trfer" />}>
                <Route path="trfer/threads" element={<ThreadsPage />} />
                <Route path="trfer/threads/:id" element={<ThreadParticipationPage />} />
              </Route>
              {/* Families is ONE list (2026-09-20). The Family queue and
                  Family reports pages folded into it on 2026-09-14 as views,
                  and the views themselves retired here. The list's route
                  sits in the trfer+admin group above since wave w1024. */}
              {/* 2026-09-20: Families is one list, so neither redirect sets a
                  view any more. Both keep their filter params, so a push deep
                  link (`?filter=escalation&urgency=critical`) still lands on a
                  truthful, filtered list rather than on a retired view. */}
              <Route path="trfer/queue" element={<SearchPreservingRedirect to="/trfer/families" />} />
              <Route path="trfer/reports" element={<SearchPreservingRedirect to="/trfer/families" />} />
              {/* The Schedule page is gone (2026-09-19, desk pages redesign
                  S1): a row was a parent's own to-do, which staff cannot act
                  on. Both old date addresses land on Families. */}
              <Route path="trfer/schedule" element={<SearchPreservingRedirect to="/trfer/families" />} />
              <Route path="trfer/calendar" element={<SearchPreservingRedirect to="/trfer/families" />} />
              <Route path="trfer/resources" element={<ResourcesPage />} />
              <Route path="trfer/circles" element={<Navigate to="/trfer/resources" replace />} />
              <Route path="trfer/community" element={<CommunityModerationPage />} />
              {/* Reimbursements — GET /reimbursements/queue is a shared
                  any-staff work queue (backend/src/routes/reimbursements.js:106),
                  NOT caseload-scoped like the rest of trfer/* (DECISIONS.md
                  2026-08-10, ledger D-4, #584 — the split that gave this page
                  its own route). The page states the shared scope on screen;
                  keep it that way. Admins reach this via their existing path. */}
              <Route path="trfer/reimbursements" element={<ReimbursementsPage />} />
              <Route path="trfer/spend" element={<SpendReviewPage />} />
              {/* Threads (companion transcripts with an escalation verdict)
                  retired 2026-10-23 — TRF is not a crisis line, and AI review
                  is admin work on /admin/reviews. A saved link lands on Today. */}
              <Route path="trfer/conversations" element={<Navigate to="/trfer" replace />} />
              <Route path="trfer/williams" element={<WilliamsPage />} />
              <Route path="trfer/settings" element={<TRFerSettingsPage />} />
              {/* #3235: Import families, reached from Settings → Applications.
                  The server admits admins and Lead TRFers (can_assign_trfers,
                  D10); the pages say so to any other TRFer and request
                  nothing. */}
              <Route path="trfer/family-imports" element={<FamilyImportsPage />} />
              <Route path="trfer/family-imports/:batchId" element={<ErrorBoundary><FamilyImportFamilyPage /></ErrorBoundary>} />
              {/* /trfer/users (User management, Pending signups, Family
                  assignments — one page since 2026-09-14) is mounted above, in
                  the admin-OR-trfer group, with its two redirects. */}
              {/* Handoff config is the Handoff tab of Settings since
                  2026-09-14 (Victor: "Handoff config can be moved to
                  Settings"). This TRFer-side address still redirects there.
                  `/admin/handoff-config` briefly redirected to the same tab
                  too (2026-09-20, admin-section subtraction) but is a real
                  page again as of 2026-09-21 (DECISIONS 2026-09-20
                  amendment) — see the route above. The two are independent
                  mounts of the same component now, not one redirecting to
                  the other. */}
              <Route path="trfer/handoff-config" element={<Navigate to="/trfer/settings?tab=handoff" replace />} />
              <Route path="trfer/polls"     element={<PollsPage scope="trfer" />} />
              <Route path="trfer/polls/new" element={<PollCreatePage scope="trfer" />} />
              <Route path="trfer/polls/:id" element={<PollDetailPage scope="trfer" />} />
              {/* #2452: the Ledger was removed 2026-10-09; old desk links land on the desk. */}
              <Route path="trfer/ledger"       element={<Navigate to="/trfer" replace />} />
              <Route path="trfer/ledger/:rest" element={<Navigate to="/trfer" replace />} />
              {/* 2026-09-14 (DECISIONS): Grant totals split into Awards (lane
                  A — what was approved) and Balances (lane B — what is left).
                  The old address redirects to Awards with its params intact,
                  the way /trfer/pipeline and /trfer/offers redirect into
                  Applications. */}
              {/* 2026-09-19 (grant awards wave): Awards is one row per award
                  and absorbed Balances, so its address redirects here too,
                  query intact. */}
              <Route path="trfer/awards" element={<AwardsPage />} />
              <Route path="trfer/balances" element={<SearchPreservingRedirect to="/trfer/awards" />} />
              <Route path="trfer/grant-totals" element={<SearchPreservingRedirect to="/trfer/awards" />} />
              {/* Placeholder surface (F-18). Ungated on purpose: it holds no
                  data, and the whole point is that every TRFer who walks the
                  prototype can read the question and answer it. */}
              <Route path="trfer/referrals" element={<ReferralCodesPage />} />
              </Route>
            </Route>
            <Route path="settings" element={<SettingsPage />} />
            <Route path="settings/legal" element={<LegalSettingsPage />} />
            {/* Folded into the /settings Notifications tab (AuditedConsentCard);
                redirect keeps old links + the deep-link working. */}
            <Route path="settings/notifications" element={<Navigate to="/settings?tab=notifications" replace />} />
            <Route path="help" element={<HelpPage />} />
            </Route>
          </Route>

          {/* TRFer mobile surface — own layout, sibling of MainLayout group, NOT MobileRouteGuard-wrapped */}
          <Route element={<RequireAuth><TrferMobileLayout /></RequireAuth>}>
            <Route element={<RoleGatedRoute allowedRoles={['trfer']} />}>
              <Route path="trfer/m" element={<TrferTodayPage />} />
              <Route path="trfer/m/family/:familyId" element={<TrferFamilyLitePage />} />
              <Route element={<LiveConversationGate fallback="/trfer/m" />}>
                <Route path="trfer/m/handoff/:id" element={<TrferHandoffMobilePage />} />
              </Route>
              <Route path="trfer/m/moderate" element={<Navigate to={isNative() ? '/trfer/m' : '/trfer/community'} replace />} />
            </Route>
          </Route>

          {/* Catch-all — a real 404 (#626). "/" itself still resolves via
              RootRoute above; unknown URLs must NOT silently re-render the
              root, that hides broken links. */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        </Suspense>
      </div>

      <Toaster />
      <MfaOnboardingModal />
    </LocaleProvider>
  );
}

export default App;
